import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { ModalsService } from '@core/modals/modals.service';
import { OktaIntegrationService } from '@core/okta-integration/services/okta-integration.service';
import {
  EVENT_DATE_TBD_TEXT,
  isEventDateTbd,
} from '@core/schedule-tbd/schedule-tbd';
import { DvmService, TutorialService } from '@core/services';
import { AnalyticsService } from '@core/services/analytics';
import { TransactionService } from '@core/services/transaction.service';
import { PrivateSellingService } from '@core/services/private-selling.service';
import { IsolatedSeatsService } from '@features/digital-venue/services/isolated-seats.service';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { UpgradesComponent } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.component';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { SaleEvent } from '../../models';
import { ConfigurationService } from '../../services/configuration.service';
import { KNOWN_ROUTE_SEGMENTS } from '../../../app-routing.module';
import { buildLegendContent } from './legend-content';

@Component({
  imports: [CommonModule, NgOptimizedImage, UpgradesComponent],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  landing: boolean;
  showLegend: boolean;
  showTutorialButton = false;

  actualPage: string | undefined;
  backBtn: 'checkout' | undefined;
  patronName: string | undefined;
  patronEmail: string | undefined;

  showUserInfo = false;
  isAuthenticated = false;

  availableColor: string;

  event: SaleEvent;
  get showDateAsTbd(): boolean {
    return isEventDateTbd(this.event);
  }
  readonly eventDateTbdText = EVENT_DATE_TBD_TEXT;

  private subscriptions: Subscription[] = [];

  private readonly DATE_FORMAT = 'EEEE, LLL d, y, h:mm a';
  private readonly WEEKDAY_FORMAT = 'EEEE';
  private readonly TIME_FORMAT = 'h:mm a';
  private readonly MONTH_DAY_FORMAT = 'LLL d';

  router = inject(Router);
  isolatedSeatsService = inject(IsolatedSeatsService);
  configurationService = inject(ConfigurationService);
  protected upgradesService: UpgradesService = inject(UpgradesService);
  private connectionService = inject(ConnectionService);
  private dvmService = inject(DvmService);
  private modalsService = inject(ModalsService);
  private tutorialService = inject(TutorialService);
  private transactionService = inject(TransactionService);
  private oktaIntegrationService = inject(OktaIntegrationService);
  private privateSellingService = inject(PrivateSellingService);
  private analyticsService = inject(AnalyticsService);

  get isTopview() {
    return this.dvmService.isTopView();
  }
  get is3dOpen() {
    return this.dvmService.is3dView();
  }
  get isIsolatedActive() {
    return this.isolatedSeatsService.isolatedSeatsActive;
  }
  get eventDateTime(): DateTime | null {
    if (!this.event?.date) {
      return null;
    }
    const hasTimezone =
      this.event.date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(this.event.date);
    if (hasTimezone) {
      return DateTime.fromISO(this.event.date);
    }
    return DateTime.fromISO(this.event.date, { zone: 'utc' });
  }
  get eventTimeZone(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.offsetNameShort || '';
  }
  get formattedEventDate(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.DATE_FORMAT);
  }
  get eventWeekday(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.WEEKDAY_FORMAT);
  }
  get eventTimeOfDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.TIME_FORMAT);
  }
  get eventMonthDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.MONTH_DAY_FORMAT);
  }
  get showPrivateSellingActions(): boolean {
    return (
      this.landing &&
      this.isAuthenticated &&
      this.privateSellingService.isEnabled()
    );
  }
  get isCartRoute(): boolean {
    return this.actualPage === 'checkout' || this.actualPage === 'summary';
  }

  get isSummaryRoute(): boolean {
    return this.actualPage === 'summary';
  }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  private initComponent() {
    const routerEventSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationEnd) {
          this.showLegend = event.url.includes('seat-selection');
          this.showTutorialButton =
            event.url.includes('seat-selection') ||
            event.url.includes('checkout');

          this.landing = event.url.includes('events');

          this.actualPage = this.resolveActivePage(event.urlAfterRedirects);
          this.backBtn =
            this.actualPage === 'checkout' ? 'checkout' : undefined;

          this.showUserInfo =
            this.actualPage === 'checkout' || this.actualPage === 'summary';

          if (this.actualPage === 'checkout') {
            this.hasTrackedCheckoutBack = false;
          }
        }
      }
    );

    const getEventSubscription = this.connectionService
      .getEvent()
      .subscribe(data => (this.event = data));

    const transactionEventSubscription =
      this.transactionService.transaction$.subscribe(wrapper => {
        const event = wrapper?.transaction?.event;
        if (event) {
          this.event = event;
        }
      });

    const authStateSubscription = this.oktaIntegrationService
      .getIsAuthenticated()
      .subscribe({
        next: (isAuthenticated: boolean) =>
          void this.onAuthStateChange(isAuthenticated),
        error: (error: unknown) => console.error(error),
      });

    this.subscriptions.push(
      routerEventSubscription,
      getEventSubscription,
      transactionEventSubscription,
      authStateSubscription
    );
  }

  private async onAuthStateChange(isAuthenticated: boolean): Promise<void> {
    this.isAuthenticated = isAuthenticated;
    if (!isAuthenticated) {
      this.patronName = undefined;
      this.patronEmail = undefined;
      return;
    }

    const patron = await this.oktaIntegrationService.getUserInfo();
    this.patronName = patron?.name ?? 'Patron name not found';
    this.patronEmail = patron?.email;
  }

  signOut(): void {
    this.oktaIntegrationService.signOut().subscribe({
      error: (error: unknown) => console.error(error),
    });
  }

  signIn(): void {
    this.oktaIntegrationService
      .signInWithRedirect(window.location.href, 'okta/redirect')
      .subscribe({ error: (error: unknown) => console.error(error) });
  }

  private hasTrackedCheckoutBack = false;

  goBack() {
    this.trackCheckoutBack();
    this.transactionService.restartTransaction();
  }

  private trackCheckoutBack(): void {
    if (this.hasTrackedCheckoutBack) {
      return;
    }
    const transaction = this.transactionService.transaction$.value?.transaction;
    if (!transaction) {
      return;
    }
    this.hasTrackedCheckoutBack = true;

    const gameId = transaction.event?.pvEventId;
    this.analyticsService.trackCheckoutBack({
      transactionId: transaction.id.toString(),
      gameId: gameId !== undefined ? String(gameId) : undefined,
      value: transaction.tdcTransaction?.balance,
      currency: 'USD',
    });
  }

  private resolveActivePage(url: string): string | undefined {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    return segments.find(segment => KNOWN_ROUTE_SEGMENTS.includes(segment));
  }

  openLegend() {
    const { phone, email } = this.configurationService.configuration;
    const modalData = {
      title: 'Location Legend',
      content: buildLegendContent({
        isTopView: this.isTopview,
        phone,
        email,
      }),
      acceptBtnName: 'CLOSE',
      compact: true,
    };
    this.modalsService.openModal(modalData);
  }

  openTutorialQuestion() {
    this.tutorialService.openTutorial();
  }

  isNotFoundRoute() {
    return this.router.url === '/not-found';
  }
}
